/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { HFlow, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { SimNaoEnum, SimNaoRadioGroupField } from 'components/form'
import { RequiredLabel } from 'components/form/final-form/RequiredLabel/RequiredLabel'
import { MetaPath } from 'util/metaPath'

import { PassouFalhouEnum } from '../model-consultaSeguimento'

export type ExemplosVerticalFieldModel<T extends string> = Record<PassouFalhouEnum, Record<T, SimNaoEnum>>

export type ExemplosVerticalFieldOptions<T extends string> = Record<
  PassouFalhouEnum,
  Array<{ value: T; label: string }>
>

interface ExemplosVerticalFieldProps<T extends string> {
  name: MetaPath<ExemplosVerticalFieldModel<T>>
  label: string
  options: ExemplosVerticalFieldOptions<T>
  title?: string
  titlePassou?: string
  titleFalhou?: string
  required?: boolean
  disabled?: boolean
}

export function ExemplosVerticalField<T extends string>(props: Readonly<ExemplosVerticalFieldProps<T>>) {
  const { name, label, options, title, titlePassou, titleFalhou, required, disabled } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  return (
    <VFlow vSpacing={0.5}>
      {required ? (
        <RequiredLabel color={disabled ? 'disabled' : 'normal'} fontWeight='bold' label={label} />
      ) : (
        <Text color={disabled ? 'disabled' : 'normal'} fontWeight='bold'>
          {label}
        </Text>
      )}
      <VFlow vSpacing={0}>
        {title && (
          <HFlow style={styles.section}>
            <div />
            <div css={styles.title}>
              <Text color='inherit'>“{title}”</Text>
            </div>
          </HFlow>
        )}
        <Box style={styles.box}>
          <HFlow style={styles.section}>
            <Text style={styles.sectionLabel}>Exemplos Passou</Text>
            <VFlow vSpacing={0} style={styles.sectionList}>
              {titlePassou && (
                <div css={styles.sectionListHeader}>
                  <Text color='inherit'>“{titlePassou}”</Text>
                </div>
              )}
              {options.PASSOU.map((item) => (
                <HFlow
                  key={item.value}
                  alignItems='center'
                  justifyContent='space-between'
                  style={styles.sectionListItem}
                >
                  <Text>{item.label}</Text>
                  <SimNaoRadioGroupField name={name.PASSOU[item.value.toString()]} />
                </HFlow>
              ))}
            </VFlow>
          </HFlow>
          <HFlow style={styles.section}>
            <Text style={styles.sectionLabel}>Exemplos Falhou</Text>
            <VFlow vSpacing={0} style={styles.sectionList}>
              {titleFalhou && (
                <div css={styles.sectionListHeader}>
                  <Text color='inherit'>“{titleFalhou}”</Text>
                </div>
              )}
              {options.FALHOU.map((item) => (
                <HFlow
                  key={item.value}
                  alignItems='center'
                  justifyContent='space-between'
                  style={styles.sectionListItem}
                >
                  <Text>{item.label}</Text>
                  <SimNaoRadioGroupField name={name.FALHOU[item.value.toString()]} />
                </HFlow>
              ))}
            </VFlow>
          </HFlow>
        </Box>
      </VFlow>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    padding: 0;
  `,
  section: css`
    grid-gap: 0;
    grid-template-columns: 5.75rem 1fr;
    &:not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.gray.c60};
    }
  `,
  title: css`
    color: ${theme.pallete.primary.main};
    font-weight: bold;
    border-radius: 2px 2px 0 0;
    border: 1px solid ${theme.pallete.divider};
    border-bottom: 0;
    padding: 0.25rem 1rem;
  `,
  sectionLabel: css`
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 1rem;
    background: ${theme.pallete.gray.c90};
  `,
  sectionList: css`
    & > div:not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.divider};
    }
  `,
  sectionListHeader: css`
    color: ${theme.pallete.primary.main};
    font-weight: bold;
    padding: 0.25rem 1rem;
  `,
  sectionListItem: css`
    padding: 0.75rem 1rem;
  `,
})
